import type { MouseEvent } from 'react'
import type { LinkProps } from 'react-router-dom'
import { createContext, use } from 'react'
import { Link } from 'react-router-dom'

/**
 * Routes opened from Benny normally use the current app router. The desktop
 * companion provides a forwarding handler instead so links focus and navigate
 * the main Scrambl window rather than replacing the companion's own route.
 */
export const AssistantNavigationContext = createContext<
  ((path: string) => void) | null
>(null)

export function useAssistantNavigation() {
  return use(AssistantNavigationContext)
}

/**
 * Where a ```scrambl-cards``` record opens. Absent in the org app, where cards
 * resolve through the organization slug. The client portal provides its own
 * resolver: a client's workspace only has routes for a few record kinds, and
 * a card with nowhere to land stays put instead of opening an org-only page.
 */
export type AssistantCardPathResolver = (card: {
  type: string
  id: string
}) => string | null

export const AssistantCardPathContext =
  createContext<AssistantCardPathResolver | null>(null)

export function useAssistantCardPath() {
  return use(AssistantCardPathContext)
}

/** Internal app link that respects the companion-to-main navigation bridge. */
export function AssistantAppLink({ to, onClick, ...props }: LinkProps) {
  const forwardNavigate = useAssistantNavigation()

  const handleClick = (event: MouseEvent<HTMLAnchorElement>) => {
    onClick?.(event)
    if (event.defaultPrevented || !forwardNavigate || typeof to !== 'string') {
      return
    }

    event.preventDefault()
    forwardNavigate(to)
  }

  return <Link to={to} onClick={handleClick} {...props} />
}
